import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Klient CRM kontextu (krok CRM1). Typy jsou generované z
 * docs/api/openapi.json — UI si `canUpdate` a `allowedTransitions` jen ČTE,
 * stavový automat se v UI nedoplňuje (viz `demand-stages.ts`).
 */

export type DemandStage = {
  readonly key: string;
  readonly label: string;
  readonly position: number;
  readonly terminal: boolean;
  readonly reopenable: boolean;
};

export type Demand = ApiPaths['/v1/crm/demands/{id}']['get']['responses']['200']['content']['application/json'];

export type DemandActivity =
  ApiPaths['/v1/crm/demands/{id}/activities']['get']['responses']['200']['content']['application/json']['items'][number];

export type ImportLeadsResult =
  ApiPaths['/v1/crm/demands/import']['post']['responses']['201']['content']['application/json'];

export type CreateDemandBody =
  ApiPaths['/v1/crm/demands']['post']['requestBody']['content']['application/json'];
export type UpdateDemandBody =
  ApiPaths['/v1/crm/demands/{id}']['patch']['requestBody']['content']['application/json'];
export type ImportLeadsBody =
  ApiPaths['/v1/crm/demands/import']['post']['requestBody']['content']['application/json'];
type PortalLead = ImportLeadsBody['leads'][number];

export async function listDemandStages(): Promise<readonly DemandStage[]> {
  const result = await unwrap(apiClient.GET('/v1/crm/demand-stages', {}));
  return result.items;
}

export interface ListDemandsParams {
  readonly stage?: string | undefined;
  readonly caseId?: string | undefined;
  readonly propertyId?: string | undefined;
  readonly partyId?: string | undefined;
  readonly ownerUserId?: string | undefined;
  readonly source?: Demand['source'] | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

/** Jeden požadavek na stránku; další stránky se v UI necházejí (viz nahlášení oříznutí). */
export async function listDemands(
  params: ListDemandsParams = {},
): Promise<{ items: Demand[]; nextCursor: string | null }> {
  return unwrap(
    apiClient.GET('/v1/crm/demands', {
      params: {
        query: {
          limit: params.limit ?? 100,
          ...(params.stage !== undefined ? { stage: params.stage } : {}),
          ...(params.caseId !== undefined ? { caseId: params.caseId } : {}),
          ...(params.propertyId !== undefined ? { propertyId: params.propertyId } : {}),
          ...(params.partyId !== undefined ? { partyId: params.partyId } : {}),
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
          ...(params.source !== undefined ? { source: params.source } : {}),
          ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        },
      },
    }),
  );
}

export async function getDemand(id: string): Promise<Demand> {
  return unwrap(apiClient.GET('/v1/crm/demands/{id}', { params: { path: { id } } }));
}

export interface CreateDemandInput {
  readonly title: string;
  readonly source: Demand['source'];
  readonly contactName?: string | undefined;
  readonly contactEmail?: string | undefined;
  readonly contactPhone?: string | undefined;
  readonly wantedKind?: string | undefined;
  readonly wantedLocality?: string | undefined;
  readonly wantedPriceFrom?: string | undefined;
  readonly wantedPriceTo?: string | undefined;
  readonly wantedDisposition?: string | undefined;
  readonly wantedAreaFrom?: string | undefined;
  readonly wantedAreaTo?: string | undefined;
  readonly caseId?: string | undefined;
  readonly propertyId?: string | undefined;
  readonly nextStep?: string | undefined;
}

/**
 * Tělo UI skládá podmínně (`...(hodnota === '' ? {} : { pole: hodnota })`),
 * takže do něj tečou i `undefined`. Generovaný kontrakt z OpenAPI volitelné
 * položky deklaruje BEZ `| undefined` a v repu je zapnuté
 * `exactOptionalPropertyTypes` — převod je tady, na jednom místě, ne v
 * každém volajícím.
 */
export async function createDemand(input: CreateDemandInput): Promise<Demand> {
  return unwrap(apiClient.POST('/v1/crm/demands', { body: input as CreateDemandBody }));
}

export interface UpdateDemandInput {
  readonly partyId?: string | undefined;
  /** Krok SR2: `null` vrátí na "Nepřečteno", ISO čas = ručně otevřeno. Kontrakt
   * z OpenAPI je bez `readAt` (generován před SR2) - `as UpdateDemandBody` je
   * zde záměrně, stejně jako u `createDemand` výš. */
  readonly readAt?: string | null | undefined;
}

export async function updateDemand(id: string, patch: UpdateDemandInput): Promise<Demand> {
  return unwrap(
    apiClient.PATCH('/v1/crm/demands/{id}', {
      params: { path: { id } },
      body: patch as UpdateDemandBody,
    }),
  );
}

export async function transitionDemand(id: string, toStage: string, note?: string): Promise<Demand> {
  return unwrap(
    apiClient.POST('/v1/crm/demands/{id}/transition', {
      params: { path: { id } },
      body: { toStage, ...(note !== undefined && note !== '' ? { note } : {}) },
    }),
  );
}

export async function listDemandActivities(id: string): Promise<readonly DemandActivity[]> {
  const result = await unwrap(apiClient.GET('/v1/crm/demands/{id}/activities', { params: { path: { id } } }));
  return result.items;
}

export async function addDemandActivity(
  id: string,
  kind: 'note' | 'email' | 'phone' | 'meeting',
  body: string,
): Promise<DemandActivity> {
  return unwrap(apiClient.POST('/v1/crm/demands/{id}/activities', { params: { path: { id } }, body: { kind, body } }));
}

export type PortalLeadInput = PortalLead;

/** Ruční import ohlasu z portálu (stejná cesta, jakou půjde dávkový import z konektoru). */
export async function importPortalLeads(leads: readonly PortalLead[]): Promise<ImportLeadsResult> {
  return unwrap(apiClient.POST('/v1/crm/demands/import', { body: { leads: [...leads] } }));
}
